/** @jsx jsx */
import { jsx } from '@emotion/react';
import { getPropertiesOfType } from 'utilities/srsUtils';
import React, { useContext, useEffect, useMemo } from 'react';
import { ObjectDropdown } from 'components/dropdowns/objectDropdown';
import { WorkspaceContext } from 'views/workspace/workspaceContext';
import { WorkspaceActionCreator } from 'hooks/useWorkspaceState';
import {
	selectDilDefinitionByName,
	PropertyType as SrsPropertyType,
	selectPropertyListFromDilByNames,
} from 'srs-middleware';
import { getReadOnlyOverride } from 'utilities/readOnlyOverrides';

interface Props {}

export const TemporalSelector: React.FunctionComponent<Props> = () => {
	const {
		state: {
			explorer: {
				selectedDefinitionName,
				filters: {
					timeline = {
						timeExtents: null,
						timeProperty: null,
					},
				},
			},
		},
		dispatch,
	} = useContext(WorkspaceContext);
	const { timeProperty } = timeline;

	const selectedDefinition = useMemo(() => {
		return selectDilDefinitionByName(selectedDefinitionName);
	}, [selectedDefinitionName]);

	const temporalProperties = useMemo(() => {
		return getPropertiesOfType(selectedDefinition, SrsPropertyType.Temporal);
	}, [selectedDefinition]);

	useEffect(() => {
		if (timeProperty || !temporalProperties) {
			return;
		}

		const defaultPropertyName = getReadOnlyOverride('defaultTemporalProperty');
		const defaultProperty = defaultPropertyName
			? selectPropertyListFromDilByNames(selectedDefinitionName, [defaultPropertyName])[0]
			: null;

		dispatch(
			WorkspaceActionCreator.updateFilter('timeline', {
				...timeline,
				timeProperty: defaultProperty ?? temporalProperties[0],
			})
		);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedDefinitionName, temporalProperties]);

	function setActiveTemporalProperty(property) {
		dispatch(
			WorkspaceActionCreator.updateFilter('timeline', {
				...timeline,
				timeProperty: property,
			})
		);
	}

	const optionToString = (x) => (x ? x.name : '');

	return (
		// @ts-ignore
		<ObjectDropdown
			options={temporalProperties || []}
			onSelectionChanged={setActiveTemporalProperty}
			activeItem={timeProperty}
			optionToString={optionToString}
			emptyText={temporalProperties.length ? 'Select a temporal property' : 'No temporal properties'}
			disabled={!temporalProperties.length}
		/>
	);
};
